import { format as dateFnsFormat } from "date-fns";
import moment from "moment";

/**
 * Centralized date formatting utility
 * Formats dates as DD-MM-YYYY throughout the application
 */

/*
 * The REST API's date-time wire format (KIN-2174/KIN-2181): ISO 8601 local time with
 * seconds and no zone, e.g. 2026-10-30T13:17:00. Every date-time the webapp sends goes out
 * in this shape. Not toISOString(): that converts to UTC and adds milliseconds and a Z,
 * and the backend rejects it. Not "YYYY-MM-DD HH:mm": the backend accepted that only for
 * one release while the webapp moved over (KIN-2182 removes it).
 */
export const API_DATE_TIME_FORMAT = "YYYY-MM-DDTHH:mm:ss";

/**
 * Formats a date to DD-MM-YYYY format
 * @param {Date|string|moment.Moment|number|Array} date - The date to format
 * @returns {string} Formatted date string in DD-MM-YYYY format
 */
export const formatDate = (date) => {
  if (!date) return "";

  try {
    if (moment.isMoment(date)) {
      return date.format("DD-MM-YYYY");
    }

    if (Array.isArray(date) && date.length >= 3) {
      const [year, month, day, hour = 0, minute = 0, second = 0] = date;

      const monthIndex = month - 1;

      const parsedArray = moment([year, monthIndex, day, hour, minute, second]);
      if (parsedArray.isValid()) {
        return parsedArray.format("DD-MM-YYYY");
      }
    }

    if (typeof date === "number") {
      const timestamp = date < 1000000000000 ? date * 1000 : date;
      return moment(timestamp).format("DD-MM-YYYY");
    }

    if (typeof date === "string") {
      if (/^\d+$/.test(date.trim())) {
        const trimmedDate = date.trim();

        if (trimmedDate.length >= 8) {
          const datePart = trimmedDate.substring(0, 8);
          const parsedNumeric = moment(datePart, "YYYYMMDD", true);
          if (parsedNumeric.isValid()) {
            return parsedNumeric.format("DD-MM-YYYY");
          }
        }

        const numericValue = parseInt(trimmedDate, 10);
        if (!isNaN(numericValue) && isFinite(numericValue)) {
          const timestamp =
            numericValue < 1000000000000 ? numericValue * 1000 : numericValue;
          const parsedTimestamp = moment(timestamp);
          if (parsedTimestamp.isValid()) {
            return parsedTimestamp.format("DD-MM-YYYY");
          }
        }
      }

      const parsedWithTime = moment(date, "DD-MM-YYYY HH:mm", true);
      if (parsedWithTime.isValid()) {
        return parsedWithTime.format("DD-MM-YYYY");
      }

      const parsedDate = moment(date, "DD-MM-YYYY", true);
      if (parsedDate.isValid()) {
        return parsedDate.format("DD-MM-YYYY");
      }

      return moment(date).format("DD-MM-YYYY");
    }

    return moment(date).format("DD-MM-YYYY");
  } catch (error) {
    console.error("Error formatting date:", error);
    return "";
  }
};

/**
 * Formats a date to DD-MM-YYYY. Named for the separator it actually produces: it was
 * `formatDateWithDashes` and its own comment read "DD-MM-YYYY format (with slashes)",
 * which cost a wrong assertion the first time someone tested it.
 * @param {Date|string|moment.Moment} date - The date to format
 * @returns {string} Formatted date string in DD-MM-YYYY format
 */
export const formatDateWithDashes = (date) => {
  if (!date) return "";

  try {
    if (moment.isMoment(date)) {
      return date.format("DD-MM-YYYY");
    }

    if (typeof date === "string") {
      const parsedDate = moment(date, "DD-MM-YYYY", true);
      if (parsedDate.isValid()) {
        return parsedDate.format("DD-MM-YYYY");
      }
    }

    return moment(date).format("DD-MM-YYYY");
  } catch (error) {
    console.error("Error formatting date:", error);
    return "";
  }
};

/**
 * Formats a date using date-fns to DD-MM-YYYY format
 * @param {Date|string} date - The date to format
 * @returns {string} Formatted date string in DD-MM-YYYY format
 */
export const formatDateFns = (date) => {
  if (!date) return "";

  try {
    const dateObj = date instanceof Date ? date : new Date(date);
    if (isNaN(dateObj.getTime())) return "";

    return dateFnsFormat(dateObj, "DD-MM-YYYY");
  } catch (error) {
    console.error("Error formatting date with date-fns:", error);
    return "";
  }
};

/**
 * Formats a date with time to DD-MM-YYYY HH:mm format
 * @param {Date|string|moment.Moment} date - The date to format
 * @returns {string} Formatted date string with time
 */
export const formatDateTime = (date) => {
  if (!date) return "";

  try {
    if (moment.isMoment(date)) {
      return date.format("DD-MM-YYYY HH:mm");
    }

    if (typeof date === "string") {
      const parsedWithTime = moment(date, "DD-MM-YYYY HH:mm", true);
      if (parsedWithTime.isValid()) {
        return parsedWithTime.format("DD-MM-YYYY HH:mm");
      }
      const parsedDate = moment(date, "DD-MM-YYYY", true);
      if (parsedDate.isValid()) {
        return parsedDate.format("DD-MM-YYYY HH:mm");
      }
    }

    return moment(date).format("DD-MM-YYYY HH:mm");
  } catch (error) {
    console.error("Error formatting date with time:", error);
    return "";
  }
};

/**
 * Formats a date with time to DD-MM-YYYY HH:mm format (with slashes)
 * @param {Date|string|moment.Moment} date - The date to format
 * @returns {string} Formatted date string with time
 */
export const formatDateTimeWithSlashes = (date) => {
  if (!date) return "";

  try {
    if (moment.isMoment(date)) {
      return date.format("DD-MM-YYYY HH:mm");
    }

    if (typeof date === "string") {
      const parsedWithTime = moment(date, "DD-MM-YYYY HH:mm", true);
      if (parsedWithTime.isValid()) {
        return parsedWithTime.format("DD-MM-YYYY HH:mm");
      }
      const parsedDate = moment(date, "DD-MM-YYYY", true);
      if (parsedDate.isValid()) {
        return parsedDate.format("DD-MM-YYYY HH:mm");
      }
    }

    return moment(date).format("DD-MM-YYYY HH:mm");
  } catch (error) {
    console.error("Error formatting date with time:", error);
    return "";
  }
};

/**
 * Formats a date to full month name format: MMMM DD, YYYY
 * @param {Date|string|moment.Moment} date - The date to format
 * @returns {string} Formatted date string
 */
export const formatDateFullMonth = (date) => {
  if (!date) return "";

  try {
    if (moment.isMoment(date)) {
      return date.format("MMMM DD, YYYY");
    }

    if (typeof date === "string") {
      const parsedDate = moment(date, "DD-MM-YYYY", true);
      if (parsedDate.isValid()) {
        return parsedDate.format("MMMM DD, YYYY");
      }
    }

    return moment(date).format("MMMM DD, YYYY");
  } catch (error) {
    console.error("Error formatting date with full month:", error);
    return "";
  }
};

/**
 * Formats a date to ISO format: YYYY-MM-DD
 * Useful for form inputs and API requests
 * @param {Date|string|moment.Moment|Array} date - The date to format
 * @returns {string} Formatted date string in YYYY-MM-DD format
 */
export const formatDateISO = (date) => {
  if (!date) return "";

  try {
    if (moment.isMoment(date)) {
      return date.format("YYYY-MM-DD");
    }

    if (Array.isArray(date) && date.length >= 3) {
      const [year, month, day] = date;
      const monthIndex = month - 1;
      const parsedArray = moment([year, monthIndex, day]);
      if (parsedArray.isValid()) {
        return parsedArray.format("YYYY-MM-DD");
      }
    }

    if (typeof date === "string") {
      if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
        return date;
      }

      const parsedDate = moment(date);
      if (parsedDate.isValid()) {
        return parsedDate.format("YYYY-MM-DD");
      }
    }

    return moment(date).format("YYYY-MM-DD");
  } catch (error) {
    console.error("Error formatting date to ISO:", error);
    return "";
  }
};

/**
 * A patient's date of birth for display, or a dash when there isn't one.
 *
 * A date of birth is optional on a patient — a walk-in or a phone booking should not be
 * blocked on paperwork (KIN-1733) — so anything that shows one has to cope with its
 * absence. Formatting a missing date directly renders the string "Invalid date", which
 * reads as a data error rather than as a field nobody has filled in yet, and appeared
 * on the patient card, the referral form and the letter form.
 *
 * Use this wherever a date of birth is shown. Whether the patient can be submitted to
 * the HSE is a separate question, answered by HseDetailsNotice.
 */
/** Shown in place of a date of birth nobody has recorded. */
const MISSING_DATE_OF_BIRTH = "—";

export const formatDateOfBirth = (date) => {
  const formatted = formatDateWithDashes(date);

  return formatted && formatted !== "Invalid date"
    ? formatted
    : MISSING_DATE_OF_BIRTH;
};

/**
 * "Firstname Surname (DOB - 12/03/1974)" — the read-only patient line several forms show.
 *
 * Four screens built this string themselves and two of them formatted the date their own
 * way, so the same patient read differently depending on which modal you opened, and a
 * missing date of birth was "N/A" in one place and "—" in another. Now that a patient may
 * legitimately have none (KIN-1733), that shows often enough to be worth one answer.
 */
export const formatPatientWithDateOfBirth = (patient) => {
  if (!patient) return "";

  const name = [patient.firstName, patient.surname].filter(Boolean).join(" ");
  const dateOfBirth = formatDateOfBirth(patient.dateOfBirth);

  // Just the name when there is no date of birth. "(DOB - —)" is a bracket holding a
  // dash, which reads worse than saying nothing — and PatientAutocomplete already drops
  // the parenthetical in that case, so this matches what the app does elsewhere.
  if (dateOfBirth === MISSING_DATE_OF_BIRTH) return name;

  return `${name} (DOB - ${dateOfBirth})`;
};

/**
 * Rewrite the bare HL7 dates that receivers put in their rejection text.
 *
 * PCRS answers with things like "not eligible on treatment/exam date [20260101] due to
 * age [20241001]" — an unpunctuated YYYYMMDD that a clinician has to decode mid-sentence,
 * in the one place they are already being told something went wrong.
 *
 * Square brackets become round on the way: the rewritten value is a readable date, not a
 * raw field the way the identifiers beside it still are, and the change in bracket makes
 * that visible at a glance.
 *
 * Only a bracketed eight-digit run starting with a plausible year is touched, and only
 * when it is a real calendar date. That leaves the other bracketed values in the same
 * sentence alone: a PPSN like [5187370B] carries a letter, and a claim number like
 * [102084949] is nine digits. An impossible date such as [20261301] is left as it is
 * rather than silently rolled forward into January.
 */
export const formatHl7DatesInText = (text) => {
  if (typeof text !== "string") return text;

  return text.replace(/\[(19|20)(\d{2})(\d{2})(\d{2})\]/g, (whole, century, year, month, day) => {
    const parsed = moment(`${century}${year}-${month}-${day}`, "YYYY-MM-DD", true);

    return parsed.isValid() ? `(${parsed.format("DD-MM-YYYY")})` : whole;
  });
};
